// Globals
import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Models
import { ICategory, ICategoryMarketplaceMapping } from '@/models/categories.model';
import { ROUTES } from '@/models/router.model';

// Components
import { TrashIcon, ChevronIcon, EditIcon, EyeIcon, CloseEyeIcon } from '@/icons';
import {
  StyledButtonWrapper,
  StyledCategoryItemMessage,
  StyledCategoryItemTitleWrapper,
  StyledCategoryItemWrapper,
  StyledChevronWrapper,
  StyledCountSlash,
  StyledMappedCount,
  StyledMappingStats,
  StyledRzBadgeWrap,
  StyledRzTreeBadge,
  StyledRzTooltip,
  StyledRzTooltipId,
  StyledRzTooltipName,
  StyledRzTooltipTitle,
  StyledTitle,
  StyledUnmappedCount,
} from './CategoryItemStyles';
import SubCategorySkeleton from '../SubCategorySkeleton/SubCategorySkeleton';
import { Link } from '@/ui';
import rozetkaMark from './rozetka-mark.png';

// Helpers
import { COLORS } from '@/theme';
import { StyledActionButton } from '@/components';

const ROZETKA = 'rozetka';

type MappingCounts = {
  mapped: number;
  unmapped: number;
};

const rozetkaMappingsOf = (category: ICategory): ICategoryMarketplaceMapping[] =>
  (category.marketplace_mappings ?? []).filter((mapping) => mapping.marketplace === ROZETKA);

const countLeafRozetkaMappings = (category: ICategory): MappingCounts => {
  const children = category.sub_categories ?? [];

  if (children.length === 0) {
    const mapped = rozetkaMappingsOf(category).length > 0;

    return { mapped: mapped ? 1 : 0, unmapped: mapped ? 0 : 1 };
  }

  return children.reduce<MappingCounts>(
    (acc, child) => {
      const stats = countLeafRozetkaMappings(child);

      return { mapped: acc.mapped + stats.mapped, unmapped: acc.unmapped + stats.unmapped };
    },
    { mapped: 0, unmapped: 0 },
  );
};

const RozetkaMark = () => (
  <img src={rozetkaMark} width={18} height={18} alt="" aria-hidden="true" style={{ display: 'block' }} />
);

const RozetkaIdBadge = ({ mappings }: { mappings: ICategoryMarketplaceMapping[] }) => {
  const { t } = useTranslation('categories');
  const first = mappings[0];

  if (!first) return null;

  const extra = mappings.length - 1;

  return (
    <StyledRzBadgeWrap onClick={(event) => event.stopPropagation()}>
      <StyledRzTreeBadge>
        {`RZ: ${first.external_id}`}
        {extra > 0 && ` +${extra}`}
      </StyledRzTreeBadge>
      <StyledRzTooltip>
        <StyledRzTooltipTitle>{t('category_rozetka_tooltip_title')}</StyledRzTooltipTitle>
        {mappings.map((mapping) => (
          <span key={mapping.external_id}>
            <StyledRzTooltipName>{mapping.name || mapping.path || mapping.external_id}</StyledRzTooltipName>
            <StyledRzTooltipId>{t('category_rozetka_tooltip_id', { id: mapping.external_id })}</StyledRzTooltipId>
          </span>
        ))}
      </StyledRzTooltip>
    </StyledRzBadgeWrap>
  );
};

type TCategoryItem = {
  category: ICategory;
  level: number;
  canManage: boolean;
  onGetSubCategories: (id: string) => void;
  onDelete: (category: ICategory) => void;
  onHide: (category: ICategory) => void;
};

const CategoryItem = ({ category, level, canManage, onGetSubCategories, onDelete, onHide }: TCategoryItem) => {
  const { t } = useTranslation('categories');

  const isFirstLevel = level === 0;
  const canBeOpen = level <= 1;
  const hasChildren = (category.sub_categories?.length ?? 0) > 0;
  const rozetkaMappings = rozetkaMappingsOf(category);
  const mappingCounts = hasChildren ? countLeafRozetkaMappings(category) : null;

  const [isOpen, setIsOpen] = useState(false);

  const handleItemClick = () => {
    if (!canBeOpen) return;

    if (level === 1 && !category?.sub_categories) {
      onGetSubCategories(category.id);
    }

    setIsOpen((prev) => !prev);
  };

  const handleView = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
  };

  const handleDelete = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onDelete(category);
  };

  const handleHide = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onHide(category);
  };

  const renderSubCategories = () => {
    if (category.is_loading) return <SubCategorySkeleton level={level} />;

    if (category.sub_categories?.length === 0)
      return (
        <StyledCategoryItemMessage $level={level + 1} $canBeOpen={canBeOpen}>
          {t('no_sub_categories')}
        </StyledCategoryItemMessage>
      );

    return category?.sub_categories?.map((subCategory) => (
      <CategoryItem
        key={subCategory.id}
        category={subCategory}
        level={level + 1}
        canManage={canManage}
        onGetSubCategories={onGetSubCategories}
        onDelete={onDelete}
        onHide={onHide}
      />
    ));
  };

  return (
    <>
      <StyledCategoryItemWrapper
        $level={level}
        $canBeOpen={canBeOpen}
        $isFirstLevel={isFirstLevel}
        onClick={handleItemClick}
      >
        <StyledCategoryItemTitleWrapper>
          <StyledChevronWrapper>
            {canBeOpen && (
              <ChevronIcon
                style={{
                  width: '1.25rem',
                  fill: '#000',
                  ...(isOpen && { transform: `rotate(180deg)` }),
                }}
              />
            )}
          </StyledChevronWrapper>
          <StyledTitle $isFirstLevel={isFirstLevel}>{category.name}</StyledTitle>
          {mappingCounts ? (
            <StyledMappingStats onClick={(event) => event.stopPropagation()}>
              <RozetkaMark />
              <StyledMappedCount>{mappingCounts.mapped}</StyledMappedCount>
              <StyledCountSlash>/</StyledCountSlash>
              <StyledUnmappedCount>{mappingCounts.unmapped}</StyledUnmappedCount>
            </StyledMappingStats>
          ) : (
            <RozetkaIdBadge mappings={rozetkaMappings} />
          )}
        </StyledCategoryItemTitleWrapper>

        {canManage && (
          <StyledButtonWrapper>
            <Link path={`${ROUTES.CATEGORIES}/${category.id}?name=${encodeURIComponent(category.name)}`}>
              <StyledActionButton $color={COLORS.PALE_YELLOW} onClick={handleView}>
                <EditIcon />
              </StyledActionButton>
            </Link>
            <StyledActionButton $color={!category.active ? COLORS.SOFT_GRAY : COLORS.PALE_YELLOW} onClick={handleHide}>
              {!category.active ? <CloseEyeIcon /> : <EyeIcon />}
            </StyledActionButton>
            <StyledActionButton $color={COLORS.RED} onClick={handleDelete}>
              <TrashIcon />
            </StyledActionButton>
          </StyledButtonWrapper>
        )}
      </StyledCategoryItemWrapper>

      {isOpen && renderSubCategories()}
    </>
  );
};

export default CategoryItem;
